import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {BrowserRouter as Router} from "react-router-dom";
import AuthContext from "./context/authContext";
import {AppRoutes} from "./app/routes";
import {AdminShell} from "./shared/layout/AdminShell";
import {LoginPage} from "./features/auth/LoginPage";
import {apiGet} from "./shared/api/client";
import {CapabilitiesProvider} from "./shared/auth/CapabilitiesContext";
import {useTranslation} from "./shared/i18n";
import {
    getAdminCountryScope,
    resolveAdminCountryScope,
    setAdminCountryScope,
} from "./shared/api/countryScope";

const normalizeCapabilities = (payload) => {
    const userId = Number(payload?.user?.id);
    const role = typeof payload?.user?.role === "string" ? payload.user.role.trim() : "";
    if (!Number.isInteger(userId) || userId <= 0 || !role) {
        throw new Error("Invalid admin capabilities response");
    }

    return {
        ...payload,
        user: {...payload.user, id: userId, role},
        modules: Array.isArray(payload.modules) ? payload.modules : [],
        scope: {
            ...(payload.scope || {}),
            country_ids: Array.isArray(payload?.scope?.country_ids)
                ? payload.scope.country_ids
                : [],
        },
    };
};

const AdminSession = ({sessionVersion, setRole}) => {
    const {t} = useTranslation();
    const [readyPrincipal, setReadyPrincipal] = useState(null);
    const [countryScope, setCountryScope] = useState(null);
    const capabilitiesQuery = useQuery({
        queryKey: ["admin-principal", sessionVersion],
        queryFn: async () =>
            normalizeCapabilities(await apiGet("/api/v2/admin/me/capabilities")),
        retry: false,
    });
    const capabilities = capabilitiesQuery.data;
    const allowedCountryIds = useMemo(
        () => capabilities?.scope?.country_ids || [],
        [capabilities?.scope?.country_ids],
    );
    const principalKey = capabilities
        ? `${sessionVersion}:${capabilities.user.id}:${capabilities.user.role}`
        : null;

    useEffect(() => {
        if (!capabilities || !principalKey) {
            setReadyPrincipal(null);
            setCountryScope(null);
            return;
        }
        const resolvedCountryScope = resolveAdminCountryScope({
            role: capabilities.user.role,
            allowedCountryIds,
            storedScope: getAdminCountryScope(),
            homeCountryId: capabilities.country?.id,
        });
        if (resolvedCountryScope === null) {
            setReadyPrincipal(null);
            setCountryScope(null);
            return;
        }
        setRole(capabilities.user.role);
        const persistedCountryScope = setAdminCountryScope(resolvedCountryScope, {
            role: capabilities.user.role,
            allowedCountryIds,
            homeCountryId: capabilities.country?.id,
        });
        setCountryScope(persistedCountryScope);
        setReadyPrincipal(principalKey);
    }, [allowedCountryIds, capabilities, principalKey, setRole]);

    const changeCountryScope = useCallback((requestedScope) => {
        if (!capabilities) return;
        const nextScope = setAdminCountryScope(requestedScope, {
            role: capabilities.user.role,
            allowedCountryIds,
            homeCountryId: capabilities.country?.id,
        });
        if (nextScope !== null) setCountryScope(nextScope);
    }, [allowedCountryIds, capabilities]);

    if (
        capabilitiesQuery.isPending ||
        (principalKey && readyPrincipal !== principalKey) ||
        (capabilities && countryScope === null)
    ) {
        return (
            <div className="admin-session-state" role="status">
                {t("shell.session.checking")}
            </div>
        );
    }

    if (capabilitiesQuery.isError || !capabilities || countryScope === null) {
        return (
            <div className="admin-session-state" role="alert">
                {t("shell.session.failed")}
                <button type="button" onClick={() => capabilitiesQuery.refetch()}>
                    {t("common.actions.retry")}
                </button>
            </div>
        );
    }

    return (
        <CapabilitiesProvider value={capabilities}>
            <AdminShell
                role={capabilities.user.role}
                capabilities={capabilities}
                countryScope={countryScope}
                onCountryScopeChange={changeCountryScope}
            >
                <AppRoutes
                    key={countryScope}
                    role={capabilities.user.role}
                    capabilities={capabilities}
                    countryScope={countryScope}
                />
            </AdminShell>
        </CapabilitiesProvider>
    );
};

function App() {
    const {auth, sessionVersion, setRole} = useContext(AuthContext);

    return (
        <Router>
            {auth ? (
                <AdminSession sessionVersion={sessionVersion} setRole={setRole} />
            ) : (
                <LoginPage />
            )}
        </Router>
    );
}

export default App;
